/* eslint camelcase: "off" */
/**
 * DocumentReviewModal — modal for setting reviewer_note + review_outcome
 * on a single VerificationDocument.
 *
 * Wired to POST /verification/admin/documents/<id>/review/. Reason is
 * required (the backend rejects empty / whitespace-only); the audit row
 * is what makes the verdict trustworthy.
 *
 * Semantics for clearing values:
 *   * Leaving review_outcome at "(unchanged)" → field is left alone.
 *   * Picking "(none)" → review_outcome is explicitly cleared.
 *   * Clearing the note text box → reviewer_note is explicitly cleared.
 *   * Leaving the note text box at its current value → leaves it alone.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, InputField } from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../../utils/apiResponseHandler';
import {
  IdentityVerificationListItem,
  ReviewOutcome,
  verificationClient,
  VerificationDocument as VerificationDocumentDetail,
} from '../../../../../services/verificationClient';


const UNCHANGED = '__unchanged__';
const CLEAR = '__clear__';

const OUTCOME_OPTIONS: Array<{ value: ReviewOutcome; label: string }> = [
  { value: 'confirm_fraud', label: 'Confirm fraud' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'accepted_with_override', label: 'Accepted with override' },
  { value: 'recapture_required', label: 'Recapture required' },
  { value: 'inconclusive', label: 'Inconclusive' },
  { value: 'escalate', label: 'Escalate' },
];


interface Props {
  /** Doc to review. Carries current reviewer_note + review_outcome so the
   *  modal can pre-fill and only send actual changes. */
  doc: Pick<
    IdentityVerificationListItem,
    'id' | 'reviewer_note' | 'review_outcome' | 'doc_type'
  >;
  onClose: () => void;
  /** Called with the updated doc after a successful submit. Caller is
   *  responsible for re-rendering with the fresh fields. */
  onSubmitted: (updated: VerificationDocumentDetail) => void;
}


const DocumentReviewModal: React.FC<Props> = ({ doc, onClose, onSubmitted }) => {
  const initialNote = doc.reviewer_note ?? '';
  const [note, setNote] = useState(initialNote);
  const [outcomeChoice, setOutcomeChoice] = useState<string>(UNCHANGED);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const verdictSelectRef = useRef<HTMLSelectElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus to the verdict select on mount — without this the
  // background page keeps focus, so screen readers and keyboard users
  // don't know the modal opened.
  useEffect(() => {
    verdictSelectRef.current?.focus();
  }, []);

  // Trap focus inside the dialog and close on Escape. Attached to the
  // document (rather than the dialog div) so it fires even when focus
  // is somewhere inside the native select dropdown. Tab cycling stays
  // within the focusable children — without this, Tab walks out into
  // the (visually hidden) background.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (submitting) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  const noteChanged = note !== initialNote;
  const outcomeChanged = outcomeChoice !== UNCHANGED;
  const hasChange = noteChanged || outcomeChanged;
  const canSubmit = hasChange && reason.trim().length > 0 && !submitting;

  const submit = useCallback(async () => {
    const payload: Parameters<typeof verificationClient.submitDocumentReview>[1] = {
      reason: reason.trim(),
    };
    if (noteChanged) {
      // Empty string → backend treats as clear; non-empty → set.
      payload.reviewer_note = note;
    }
    if (outcomeChanged) {
      payload.review_outcome =
        outcomeChoice === CLEAR ? null : (outcomeChoice as ReviewOutcome);
    }
    setSubmitting(true);
    try {
      const updated = await verificationClient.submitDocumentReview(doc.id, payload);
      onSubmitted(updated);
      onClose();
    } catch (err) {
      apiResponseHandler.handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  }, [reason, note, noteChanged, outcomeChanged, outcomeChoice, doc.id, onSubmitted, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-review-modal-title"
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl bg-white p-md"
      >
        <h4 id="document-review-modal-title" className="mb-xs text-sm font-semibold">
          Review {doc.doc_type === 'selfie' ? 'selfie' : 'document'}
        </h4>
        <p className="mb-sm text-xs text-greyscale-text-caption">
          Both fields are optional but you must change at least one. A reason is required
          and lands on the audit log with your verdict.
        </p>

        <div className="flex flex-col gap-sm">
          <label htmlFor="document-review-verdict" className="flex flex-col gap-xxs">
            <span className="text-xs text-greyscale-text-caption">Verdict</span>
            <select
              id="document-review-verdict"
              ref={verdictSelectRef}
              className="rounded-md border border-greyscale-border-default px-xs py-xxs text-xs"
              value={outcomeChoice}
              onChange={(e) => setOutcomeChoice(e.target.value)}
            >
              <option value={UNCHANGED}>
                (unchanged — currently {doc.review_outcome || 'none'})
              </option>
              {doc.review_outcome && <option value={CLEAR}>(clear current verdict)</option>}
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <InputField
            label="Reviewer note (optional)"
            value={note}
            placeholder="Empty to clear the existing note"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
          />

          <InputField
            label="Reason (required)"
            value={reason}
            placeholder="Why this verdict — captured to the audit log"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          />
        </div>

        <div className="mt-sm flex justify-end gap-xxs">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={!canSubmit}>
            {submitting ? <Loader2 className="animate-spin" size={14} /> : 'Submit review'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentReviewModal;
